import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { firstValueFrom } from 'rxjs';

import { computed, inject } from '@angular/core';

import { ResourcesService, ToastService, UserPreferenceKey, UserPreferencesService } from '@mitsubishi/core';
import { SpinnerService } from '@mitsubishi/ui/spinner';
import { ApmGridColumnType } from '@mitsubishi/ui/table';
import { getColorWithOpacity } from '@mitsubishi/utils';

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { Preset, SystemUsagePlanPresetRequest } from '../../models/energy-analysis-preset.model';
import { EnergyAnalysisRule, EnergyAnalysisRulesRequest } from '../../models/energy-analysis-rules.model';
import {
  System,
  SystemRequest,
  SystemResponse,
  SystemUnit,
  SystemUnitRequest,
  SystemsColors,
  UpdateSystemRequest,
  UpdateSystemUnitRequest,
  UpdateSystemsComparisonSettingsRequest,
  systemsComparisonSectionSettings,
} from '../../models/energy-analysis-system.model';
import {
  EconomicCurvesChart,
  GroupComparisonInfo,
  LatestSystemsEnergyAnalysisTasksResponse,
  PaybackInfo,
  PerformanceData,
  SystemSvcResultField,
  SystemsTotals,
  Task,
} from '../../models/energy-analysis-task.model';
import {
  CapacityCurveData,
  CapacityCurveLabel,
  ClimateDataRequest,
  EnergyAnalysesList,
  EnergyAnalysis,
  EnergyAnalysisConfig,
  EnergyAnalysisEconomicDefaults,
  EnergyAnalysisFilters,
  EnergyAnalysisListRequest,
  EnergyAnalysisSorting,
  SeasonChartData,
  SystemUsagePlanBinsTableColumn,
  SystemUsagePlanHistogramData,
  UpdateEnergyAnalysisRequest,
  UpdateManualBinsTable,
  cityChartData,
} from '../../models/energy-analysis.model';
import { EnergyAnalysisService } from '../../services/energy-analysis.service';
import { buildEconomicCurvesChart } from '../../utils/economic-analysis-charts.utils';
import { formatEaNumber, getDetailsDecimals } from '../../utils/energy-analysis.utils';

export interface EnergyAnalisyListStateModel {
  paginatedData: Partial<EnergyAnalysesList>;
  currentFilters: EnergyAnalysisFilters;
  currentSorting: EnergyAnalysisSorting;
  currentPage: number;
  pageSize: number;
  isFetching: boolean;
  activeEnergyAnalysis: EnergyAnalysis | null;
  activeEnergyAnalysisConfig: EnergyAnalysisConfig | null;
  // While a currency change is repricing+reloading, holds the PREVIOUS currency code so displayed price
  // symbols stay pinned to the old currency until the converted values arrive (avoids new symbol / old values).
  displayCurrencyCodeOverride: string | null;
  economicParameters: UpdateEnergyAnalysisRequest | null;
  allowSaveEconomicParameters: boolean;
  rowsManualBinsTable: { [key: string]: number }[];
  allowSaveManualBinsTable: boolean;
  cityClimateDataParams: ClimateDataRequest;
  rules: EnergyAnalysisRule[];
  presets: Preset[];
  systems: SystemResponse;
  activeSystem: System | null;
  systemUnits: SystemUnit[];
  totalSystemUnits: number;
  activeSystemUnit: SystemUnit | null;
  systemUsagePlanGrid: any;
  systemUsagePlanHistogram: SystemUsagePlanHistogramData[];
  systemUsagePlanBinsData: { [key: string]: number }[];
  systemUsagePlanBinsColumnsDefinitions: SystemUsagePlanBinsTableColumn[];
  systemUsagePlanBinsTotalOperatingHours: number;
  capacityCurveData: CapacityCurveData[];
  capacityCurveDataLabels: CapacityCurveLabel | null;
  cityClimateData: cityChartData[];
  activeTaskPerformanceCol: any[];
  taskPerformanceFields: SystemSvcResultField | null;
  tasksPerformanceData: PerformanceData[];
  taskStatusId: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  taskPerformanceColDef: ColDef[];
  energyAnalysisPaybackFields: any[];
  energyAnalysisPaybackData: { [key: string]: number | string | null }[];
  energyAnalysisPaybackInfo: PaybackInfo | null;
  energyAnalysisGroupComparisonColumns: {
    valueYField: string;
    categoryXField: string;
    label?: string;
    color?: string;
  }[];
  energyAnalysisGroupComparisonData: { [key: string]: number | string | null }[];
  energyAnalysisGroupComparisonInfo: GroupComparisonInfo | null;
  energyAnalysisGroupComparisonCumulativeChart: EconomicCurvesChart | null;
  energyAnalysisGroupComparisonCashFlowChart: EconomicCurvesChart | null;
  systemsTotalsFields: {
    field: number;
    idResource: number;
    unit: string;
  }[];
  systemsTotals: SystemsTotals[];
  // to remove with socket
  activeTaskStatus: Task | null;
  svcRequests: { svcRequest: any; systemId: string }[];
  systemSvcResults: { systemSvcResult: any; systemId: string }[];
  systemSvcResultCurves: {
    systemName: string;
    systemId: string;
    curves: any[];
  }[];
  invalidatedTasks: boolean;
  systemWorkingModes: { id: string; name: string }[];
  systemsColors: SystemsColors;
  systemsComparisonSettings: systemsComparisonSectionSettings[];
}

const initialState: EnergyAnalisyListStateModel = {
  paginatedData: {},
  currentFilters: {},
  currentSorting: {},
  currentPage: 1,
  pageSize: 20,
  isFetching: false,
  activeEnergyAnalysis: null,
  activeEnergyAnalysisConfig: null,
  displayCurrencyCodeOverride: null,
  economicParameters: null,
  allowSaveEconomicParameters: false,
  rowsManualBinsTable: [],
  allowSaveManualBinsTable: false,
  cityClimateDataParams: {
    type: 'ANNUAL_HOURLY_TEMPERATURE',
    contentRepresentation: 'HISTOGRAM',
    binSize: 1,
  },
  rules: [],
  presets: [],
  systems: {
    elements: [],
    totalElements: 0,
    totalPages: 0,
  },
  systemUnits: [],
  totalSystemUnits: 0,
  activeSystem: null,
  activeSystemUnit: null,
  systemUsagePlanGrid: [],
  systemUsagePlanHistogram: [],
  systemUsagePlanBinsData: [],
  systemUsagePlanBinsColumnsDefinitions: [],
  systemUsagePlanBinsTotalOperatingHours: 0,
  capacityCurveData: [],
  capacityCurveDataLabels: null,
  cityClimateData: [],
  activeTaskPerformanceCol: [],
  taskPerformanceFields: null,
  tasksPerformanceData: [],
  taskStatusId: 0,
  taskPerformanceColDef: [],
  energyAnalysisPaybackFields: [],
  energyAnalysisPaybackData: [],
  energyAnalysisPaybackInfo: null,
  energyAnalysisGroupComparisonColumns: [],
  energyAnalysisGroupComparisonData: [],
  energyAnalysisGroupComparisonInfo: null,
  energyAnalysisGroupComparisonCumulativeChart: null,
  energyAnalysisGroupComparisonCashFlowChart: null,
  systemsTotalsFields: [],
  systemsTotals: [],
  activeTaskStatus: null,
  svcRequests: [],
  systemSvcResults: [],
  systemSvcResultCurves: [],
  invalidatedTasks: false,
  systemWorkingModes: [],
  systemsColors: {},
  systemsComparisonSettings: [],
};

export const EnergyAnalisesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store, resourcesService = inject(ResourcesService)) => ({
    energyAnalysisRequestParameters: computed<EnergyAnalysisListRequest>(() => {
      const filters = store.currentFilters();
      const sorting = store.currentSorting();
      return {
        ...filters,
        ...sorting,
        page: store.currentPage(),
        pageSize: store.pageSize(),
      };
    }),
    paginationHasNextPage: computed(() => {
      const totalPages = store.paginatedData()?.totalPages ?? 0;
      return store.currentPage() < totalPages;
    }),
    /**
     * Grid state scope of the performance grids, View Results and the Performances tab (APPLYME-6304):
     * one per analysis type, column ids without the system id.
     */
    performanceGridId: computed(() => {
      const systemTypeId = store.activeEnergyAnalysis()?.systemUsageType?.id;
      return systemTypeId ? `ea-performance-details-${systemTypeId}` : '';
    }),
    energyAnalysisRulesRequestParameters: computed<EnergyAnalysisRulesRequest | null>(() => {
      if (!store.activeEnergyAnalysis()) return null;
      return {
        page: 1,
        pageSize: 80,
        orderBy: 'CREATED_AT',
        orderDirection: 'DESC',
        analysesIds: [store.activeEnergyAnalysis()!.id],
      };
    }),
    presetRequestParameters: computed<SystemUsagePlanPresetRequest>(() => ({
      page: 1,
      pageSize: 80,
      orderBy: 'CREATED_AT',
      orderDirection: 'DESC',
      analysisId: store.activeEnergyAnalysis()?.id,
    })),
    seasonChartData: computed<SeasonChartData[]>(() => {
      interface SeasonRanges {
        startRange1: number | null;
        endRange1: number | null;
        startRange2?: number | null;
        endRange2?: number | null;
      }

      const SEASON_MAP = {
        COOLING: {
          label: resourcesService.getTextResource('451641'),
          color: '#A1DAE8',
        },
        HEATING: {
          label: resourcesService.getTextResource('451637'),
          color: '#FFA1A8',
        },
      };

      function isMainSeasonCrossingYearEnd(altSeasonStart: string | null, altSeasonEnd: string | null): boolean {
        if (altSeasonStart && altSeasonEnd) {
          const altStart = createDate(altSeasonStart);
          const altEnd = createDate(altSeasonEnd);
          return altStart > altEnd;
        } else {
          return false;
        }
      }

      function createDate(date: string): number {
        const dateParts = date.substring(2).split('-');
        const month = parseInt(dateParts[0]) - 1;
        const day = parseInt(dateParts[1]);
        return new Date(2018, month, day).getTime();
      }

      function calculateCrossYearRanges(altSeasonStart: string, altSeasonEnd: string): SeasonRanges {
        const yearStart = new Date(2018, 0, 1).getTime(); // 1 gennaio
        const yearEnd = new Date(2018, 11, 31).getTime(); // 31 dicembre

        const altStart = createDate(altSeasonStart);
        const altEnd = createDate(altSeasonEnd);

        // Range 1: da inizio anno al giorno prima di altStart
        // Range 2: dal giorno dopo altEnd a fine anno
        const range1End = altStart - 24 * 60 * 60 * 1000;
        const range2Start = altEnd + 24 * 60 * 60 * 1000;

        return {
          startRange1: yearStart,
          endRange1: range1End,
          startRange2: range2Start,
          endRange2: yearEnd,
        };
      }

      const data: SeasonChartData[] = [];
      if (store.activeEnergyAnalysis() && store.activeEnergyAnalysis()!.defaultSeason) {
        const mainSeason = store.activeEnergyAnalysis()!.defaultSeason!;
        const alternativeSeason = mainSeason === 'HEATING' ? 'COOLING' : 'HEATING';

        // definition of alternative season range
        let alternativeSeasonStart = store.activeEnergyAnalysis()!.altSeasonStart;
        let alternativeSeasonEnd = store.activeEnergyAnalysis()!.altSeasonEnd;

        // definition of main season range
        let mainRanges: SeasonRanges | null = null;

        let alternativeRanges: SeasonRanges | null = null;

        // is empty alternative season
        if (!alternativeSeasonStart || !alternativeSeasonEnd) {
          mainRanges = {
            startRange1: createDate('--01-01'),
            endRange1: createDate('--12-31'),
          };
          alternativeRanges = {
            startRange1: null,
            endRange1: null,
          };
        } else {
          if (isMainSeasonCrossingYearEnd(alternativeSeasonStart, alternativeSeasonEnd)) {
            alternativeRanges = calculateCrossYearRanges(
              store.activeEnergyAnalysis()!.altSeasonEnd!,
              store.activeEnergyAnalysis()!.altSeasonStart!
            );
            mainRanges = {
              startRange1: createDate(alternativeSeasonStart!),
              endRange1: createDate(alternativeSeasonEnd!),
            };
          } else {
            mainRanges = calculateCrossYearRanges(
              store.activeEnergyAnalysis()!.altSeasonStart!,
              store.activeEnergyAnalysis()!.altSeasonEnd!
            );
            alternativeRanges = {
              startRange1: createDate(alternativeSeasonStart!),
              endRange1: createDate(alternativeSeasonEnd!),
            };
          }
        }

        // main season row
        const mainSeasonData: SeasonChartData = {
          season: SEASON_MAP[mainSeason].label,
          columnSettings: {
            stroke: SEASON_MAP[mainSeason].color,
            fill: SEASON_MAP[mainSeason].color,
          },
          ...mainRanges,
        };
        data.push(mainSeasonData);

        // alternative season row
        const alternativeSeasonData: SeasonChartData = {
          season: SEASON_MAP[alternativeSeason].label,
          columnSettings: {
            stroke: SEASON_MAP[alternativeSeason].color,
            fill: SEASON_MAP[alternativeSeason].color,
          },
          ...alternativeRanges,
        };
        data.push(alternativeSeasonData);

        // always put cooling season at top
        data.sort((a, b) => {
          if (a.season === SEASON_MAP.COOLING.label) return 1;
          if (b.season === SEASON_MAP.COOLING.label) return -1;
          return 0;
        });
      }

      return data;
    }),
    systemRequestParameters: computed<SystemRequest | null>(() => {
      if (!store.activeEnergyAnalysis()) return null;
      return {
        page: 1,
        pageSize: 80,
        orderBy: 'CREATED_AT',
        orderDirection: 'DESC',
        analysesIds: [store.activeEnergyAnalysis()!.id],
      };
    }),
    systemUnitRequestParameters: computed<SystemUnitRequest | null>(() => {
      if (!store.activeSystem()) return null;
      return {
        page: 1,
        pageSize: 80,
        orderBy: 'CREATED_AT',
        orderDirection: 'DESC',
        systemsIds: [store.activeSystem()!.id],
      };
    }),

    mainHistogramData: computed(() => {
      const mergeByTemperature = (...arrays: any[]) => {
        const map = new Map();

        for (const arr of arrays) {
          for (const obj of arr) {
            const key = obj.temperature;
            if (!map.has(key)) {
              map.set(key, { temperature: key });
            }
            Object.assign(map.get(key), obj);
          }
        }
        const list = Array.from(map.values()).sort((a, b) => a.temperature - b.temperature);
        return list;
      };

      const histogram = [...store.systemUsagePlanHistogram()];
      let background = [...store.cityClimateData()];
      const linear =
        store.systemUsagePlanBinsData()?.map(row => {
          return {
            temperature: row.temperature,
            coolingPower: row.cooling_capacity_ratio * 100,
            thermalPower: row.heating_capacity_ratio * 100,
          };
        }) || [];

      // add backgroud points only for existing temperature in histogram
      if (linear.length > 0) {
        background = background.filter(dataPoint => {
          const existingTemperatre = linear.find(lp => lp.temperature === dataPoint.temperature);
          return existingTemperatre !== undefined;
        });
      }

      const mainChart = mergeByTemperature(background, histogram, linear);
      return mainChart;
    }),

    selectedSystemsIds: computed<string[]>(() => {
      const selectedIds: string[] = [];
      store.systemsComparisonSettings().forEach(setting => {
        if (setting.isSelected) {
          selectedIds.push(setting.systemId);
        }
      });
      return selectedIds;
    }),
  })),
  withMethods(
    (
      store,
      energyAnalysesService = inject(EnergyAnalysisService),
      spinnerService = inject(SpinnerService),
      resourcesService = inject(ResourcesService),
      userPreferencesService = inject(UserPreferencesService),
      toastService = inject(ToastService)
    ) => {
      const loadEnergyAnalyses = () => {
        const isFirstPage = store.currentPage() === 1;
        if (isFirstPage) {
          spinnerService.showMainSpinner();
        }
        patchState(store, { isFetching: true });
        energyAnalysesService.searchEnergyAnalyses(store.energyAnalysisRequestParameters()).subscribe({
          next: response => {
            if (isFirstPage) {
              spinnerService.hideMainSpinner();
            }
            patchState(store, {
              paginatedData: {
                ...response,
                elements: [
                  ...(!isFirstPage ? (store.paginatedData()?.elements ?? []) : []),
                  ...response.elements,
                ],
              },
              isFetching: false,
            });
          },
          error: error => {
            if (isFirstPage) {
              spinnerService.hideMainSpinner();
            }
            patchState(store, { isFetching: false });
            console.error('Error loading energy analyses list', error);
          },
        });
      };

      const updateEnergyAnalysisFilters = (filters: EnergyAnalysisFilters) => {
        patchState(store, { currentFilters: filters, currentPage: 1 });
      };

      const getEnergyAnalysis = (energyAnalysisId: string) => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.getEnergyAnalysis(energyAnalysisId).subscribe({
            next: response => {
              patchState(store, {
                activeEnergyAnalysis: response as EnergyAnalysis,
              });
              resolve();
            },
            error: error => {
              console.error('Error loading energy analyses list', error);
              reject(error);
            },
          });
        });
      };

      const getEnergyAnalysisConfig = (energyAnalysisId: string) => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.getEnergyAnalysisConfig(energyAnalysisId).subscribe({
            next: response => {
              patchState(store, {
                activeEnergyAnalysisConfig: response as EnergyAnalysisConfig,
              });
              resolve();
            },
            error: error => {
              console.error('Error loading energy analyses config', error);
              reject(error);
            },
          });
        });
      };

      const loadEnergyAnalysisSystemUsagePlanBinsTable = () => {
        if (!store.activeEnergyAnalysis()) return;

        return new Promise<{ [key: string]: number }[]>((resolve, reject) => {
          spinnerService.showMainSpinner();
          energyAnalysesService.getEnergyAnalysisSystemUsagePlanBinsTable(store.activeEnergyAnalysis()!.id).subscribe({
            next: response => {
              spinnerService.hideMainSpinner();
              patchState(store, {
                systemUsagePlanBinsColumnsDefinitions: response.columnsDefinitions || [],
                systemUsagePlanBinsData: response.rows,
                systemUsagePlanBinsTotalOperatingHours: response.totalOperatingHours ?? 0,
              });
              resolve(response.rows);
            },
            error: error => {
              console.error('Error loading system usage plan bins table', error);
              spinnerService.hideMainSpinner();
              reject(error);
            },
          });
        });
      };

      const updateEnergyAnalysis = (
        energyAnalysisId: string,
        createEnergyAnalysisRequest: UpdateEnergyAnalysisRequest
      ) => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.updateEnergyAnalysis(energyAnalysisId, createEnergyAnalysisRequest).subscribe({
            next: response => {
              patchState(store, {
                activeEnergyAnalysis: response as EnergyAnalysis,
              });
              resolve();
            },
            error: error => {
              console.error('Error updating energy analysis', error);
              reject(error);
            },
          });
        });
      };

      /**
       * APPLYMESD-2538: salva i parametri della sezione General come default personale, letto dal
       * backend alla creazione di una nuova analisi. Non tocca l'analisi corrente né il suo Save.
       * Chiamata diretta al servizio: `UserPreferencesStore` fa debounce e assorbe gli errori, qui
       * servono i messaggi di conferma e di errore.
       */
      const saveEconomicDefaults = (economicDefaults: EnergyAnalysisEconomicDefaults) => {
        userPreferencesService.upsert(UserPreferenceKey.EA_ECONOMIC_DEFAULTS, null, economicDefaults).subscribe({
          next: () => toastService.success({ resourceId: '520903' }),
          error: () => toastService.error({ resourceId: '520904' }),
        });
      };

      const getSystemEconomicDefaults = (energyAnalysisId: string): Promise<EnergyAnalysisEconomicDefaults> =>
        firstValueFrom(energyAnalysesService.getSystemEconomicDefaults(energyAnalysisId));

      const setEconomicParameters = (economicParameters: UpdateEnergyAnalysisRequest) => {
        patchState(store, {
          economicParameters: economicParameters,
        });
      };

      const setAllowSaveEconomicParameters = (allow: boolean) => {
        patchState(store, {
          allowSaveEconomicParameters: allow,
        });
      };

      const setRowsManualBinsTable = (rows: { [key: string]: number }[]) => {
        patchState(store, {
          rowsManualBinsTable: rows,
        });
      };

      const setAllowSaveManualBinsTable = (allow: boolean) => {
        patchState(store, {
          allowSaveManualBinsTable: allow,
        });
      };

      const loadSysthemUsagePlanGrid = () => {
        spinnerService.showMainSpinner();
        if (!store.activeEnergyAnalysis()) return;

        energyAnalysesService.getSystemUsagePlanGrid(store.activeEnergyAnalysis()!.id).subscribe({
          next: response => {
            spinnerService.hideMainSpinner();

            patchState(store, {
              systemUsagePlanGrid: response.data,
            });
          },
          error: error => {
            console.error('Error loading system usage plan grid', error);
          },
        });
      };
      const loadHeatingCapacityCurve = () => {
        return new Promise<{
          heatingCurve: { [key: number]: number };
          labels: CapacityCurveLabel;
        }>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;

          energyAnalysesService.getEnergyAnalysisHeatingCapacityCurve(store.activeEnergyAnalysis()!.id).subscribe({
            next: response => {
              if (response) {
                resolve({
                  heatingCurve: response?.data || {},
                  labels: {
                    keyLabel: response.keyLabel,
                    keyUom: response.keyUom,
                    valueLabel: response.valueLabel,
                    valueUom: response.valueUom,
                  },
                });
              }
            },
            error: error => {
              console.error('Error loading heating capacity curve', error);
              reject(error);
            },
          });
        });
      };

      const loadCoolingCapacityCurve = () => {
        return new Promise<{
          coolingCurve: {
            [key: number]: number;
          };
          labels: CapacityCurveLabel;
        }>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;

          energyAnalysesService.getEnergyAnalysisCoolingCapacityCurve(store.activeEnergyAnalysis()!.id).subscribe({
            next: response => {
              if (response) {
                resolve({
                  coolingCurve: response?.data || {},
                  labels: {
                    keyLabel: response.keyLabel,
                    keyUom: response.keyUom,
                    valueLabel: response.valueLabel,
                    valueUom: response.valueUom,
                  },
                });
              }
            },
            error: error => {
              console.error('Error loading cooling capacity curve', error);
              reject(error);
            },
          });
        });
      };
      const updateCoolingCapacityCurve = (updateCapacityCurve: { key: number; value: number }) => {
        return new Promise<{
          coolingCurve: { [key: number]: number };
        }>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;
          energyAnalysesService
            .updateEnergyAnalysisCoolingCapacityCurve(store.activeEnergyAnalysis()!.id, updateCapacityCurve)
            .subscribe({
              next: response => {
                resolve({
                  coolingCurve: response.data,
                });
              },
              error: error => {
                console.error('Error loading cooling capacity curve', error);
                reject(error);
              },
            });
        });
      };

      const updateManualBinsTable = (updateManualBinsTable: UpdateManualBinsTable[]) => {
        return new Promise<{ systemUsagePlanBinsData: { [key: string]: number }[] }>((resolve, reject) => {
          spinnerService.showMainSpinner();

          if (!store.activeEnergyAnalysis()) return;
          energyAnalysesService
            .updateManualClimateDataBinsTable(store.activeEnergyAnalysis()!.id, updateManualBinsTable)
            .subscribe({
              next: response => {
                spinnerService.hideMainSpinner();
                patchState(store, {
                  systemUsagePlanBinsData: response.content.rows,
                });

                resolve({
                  systemUsagePlanBinsData: response.content.rows,
                });
              },
              error: error => {
                spinnerService.hideMainSpinner();
                console.error('Error loading cooling capacity curve', error);
                reject(error);
              },
            });
        });
      };

      const updateHeatingCapacityCurve = (updateCapacityCurve: { key: number; value: number }) => {
        return new Promise<{
          heatingCurve: { [key: number]: number };
        }>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;
          energyAnalysesService
            .updateEnergyAnalysisHeatingCapacityCurve(store.activeEnergyAnalysis()!.id, updateCapacityCurve)
            .subscribe({
              next: response => {
                resolve({
                  heatingCurve: response.data,
                });
              },
              error: error => {
                console.error('Error loading heating capacity curve', error);
                reject(error);
              },
            });
        });
      };

      const loadEnergyAnalysisRules = () => {
        if (store.energyAnalysisRulesRequestParameters()) {
          energyAnalysesService.searchRules(store.energyAnalysisRulesRequestParameters()!).subscribe({
            next: response => {
              patchState(store, {
                rules: response.elements,
              });
            },
            error: error => {
              console.error('Error loading energy analyses list', error);
            },
          });
        }
      };
      const loadPresets = () => {
        energyAnalysesService.searchSystemUsagePlanPreset(store.presetRequestParameters()).subscribe({
          next: response => {
            patchState(store, {
              presets: response.elements,
            });
          },
          error: error => {
            console.error('Error loading cities', error);
          },
        });
      };

      const onlyDeletePreset = (presetId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.deleteSystemUsagePlanPreset(presetId).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error deleting preset', error);
              reject(error);
            },
          });
        });
      };

      const loadPreset = (presetId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          if (!store.activeEnergyAnalysis()) return;
          energyAnalysesService.copyFromPreset(store.activeEnergyAnalysis()!.id, presetId).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error loading preset', error);
              reject(error);
            },
          });
        });
      };

      const createPreset = (presetName: string): Promise<void> => {
        const prameters = {
          name: presetName,
          rules: store.rules(),
        };
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.createSystemUsagePlanPreset(prameters).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error creating preset', error);
              reject(error);
            },
          });
        });
      };

      const loadSystemUsagePlanHistogram = (): void => {
        if (!store.activeEnergyAnalysis()) return;
        energyAnalysesService.getSystemUsagePlanHistogram(store.activeEnergyAnalysis()!.id).subscribe({
          next: response => {
            const systemUsagePlan: SystemUsagePlanHistogramData[] = response.bins.map((item: any) => ({
              temperature: item.key,
              cooling: item.value.COOLING,
              heating: item.value.HEATING,
              simultaneous: item.value.COOLING_AND_HEATING,
            }));

            // Sort systemUsagePlan by temperature in ascending order
            const sortedSystemUsagePlan = [...systemUsagePlan].sort((a, b) => a.temperature - b.temperature);

            patchState(store, {
              systemUsagePlanHistogram: sortedSystemUsagePlan,
            });
          },
          error: error => {
            console.error('Error loading system usage plan histogram', error);
          },
        });
      };

      const loadSystems = () => {
        return new Promise<void>((resolve, reject) => {
          spinnerService.showMainSpinner();

          if (store.systemRequestParameters()) {
            energyAnalysesService.searchSystem(store.systemRequestParameters()!).subscribe({
              next: response => {
                spinnerService.hideMainSpinner();

                // Mappa i systems ai colori
                const systemsColors: SystemsColors = {};
                response.elements.forEach((system, index) => {
                  systemsColors[system.id] = {
                    value: system.color,
                    active: true,
                  };
                });

                patchState(store, {
                  systems: response,
                  systemsColors,
                });
                resolve();
              },
              error: error => {
                console.error('Error loading cities', error);
              },
            });
          }
        });
      };

      const getEnergyAnalysisSystem = (systemId: string) => {
        return new Promise<void>((resolve, reject) => {
          spinnerService.showMainSpinner();

          energyAnalysesService.getSystem(systemId).subscribe({
            next: response => {
              spinnerService.hideMainSpinner();
              patchState(store, {
                activeSystem: response as System,
                systems: {
                  elements: [response as System],
                  totalElements: 1,
                  totalPages: 1,
                },
              });
              resolve();
            },
            error: error => {
              // Without this the overlay stays up forever and locks the page the request was fired from.
              spinnerService.hideMainSpinner();
              console.error('Error loading energy analyses list', error);
              reject(error);
            },
          });
        });
      };

      const updateSystem = (systeId: string, updateSystem: UpdateSystemRequest): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.updateSystem(systeId, updateSystem).subscribe({
            next: response => {
              patchState(store, {
                activeSystem: response as System,
              });
              resolve();
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };

      const deleteSystem = (systemId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.deleteSystem(systemId).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error deleting system', error);
              reject(error);
            },
          });
        });
      };

      const copySystem = (systemId: string): Promise<System> => {
        return new Promise<System>((resolve, reject) => {
          energyAnalysesService.copySystem(systemId).subscribe({
            next: copy => {
              resolve(copy);
            },
            error: error => {
              console.error('Error copying system', error);
              reject(error);
            },
          });
        });
      };

      const searchSystemUnits = (): void => {
        energyAnalysesService.searchSystemUnit(store.systemUnitRequestParameters()!).subscribe({
          next: response => {
            patchState(store, {
              systemUnits: response.elements,
              totalSystemUnits: response.totalElements,
            });
          },
          error: error => {
            console.error('Error loading system units', error);
          },
        });
      };

      const getEnergyAnalysisSystemUnit = (systemUnitId: string) => {
        return new Promise<void>((resolve, reject) => {
          spinnerService.showMainSpinner();

          energyAnalysesService.getSystemUnit(systemUnitId).subscribe({
            next: response => {
              spinnerService.hideMainSpinner();
              patchState(store, {
                activeSystemUnit: response as SystemUnit,
              });
              resolve();
            },
            error: error => {
              console.error('Error loading system unit', error);
              reject(error);
            },
          });
        });
      };
      const deleteSystemUnit = (unitId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.deleteSystemUnit(unitId).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };

      const updateSystemUnit = (unitId: string, updateSystemUnit: UpdateSystemUnitRequest): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.updateSystemUnit(unitId, updateSystemUnit).subscribe({
            next: () => {
              resolve();
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };

      const getColumnsForSystemTaskPerformance = (system: System, hasBoiler: boolean, hasUncoveredPF: boolean, hasUncoveredPT: boolean): string[] => {
        let fieldsids: string[];
        switch (store.activeEnergyAnalysis()?.systemUsageType.id) {
          // cold
          case '1':
            if (system?.unitsFlags?.hasChillerEFC) {
              fieldsids = ['110031', '110043', '110035', '110036', '110056', '110062'];
            } else if (system?.unitsFlags?.hasChillerFC) {
              fieldsids = ['110031', '110043', '110035', '110036', '110056', '110062'];
            } else {
              fieldsids = ['110031', '110035', '110036', '110056', '110062'];
            }
            break;
          // hot
          case '2':
            if (hasBoiler) {
              fieldsids = [
                '110038',
                '110035',
                '110036',
                '110046',
                '110047',
                '110059',
                '110060',
                '110040',
                '110050',
                '110063',
              ];
            } else {
              fieldsids = ['110038', '110035', '110036', '110059', '110063'];
            }
            break;
          // cold+hot
          case '3':
            fieldsids = ['110031'];
            if (system?.unitsFlags?.hasChillerFC) {
              fieldsids.push('110043');
            }
            if (hasBoiler) {
              fieldsids.push(
                '110038',
                '110035',
                '110036',
                '110046',
                '110047',
                '110057',
                '110058',
                '110040',
                '110050',
                '110062',
                '110063'
              );
            } else {
              fieldsids.push('110038', '110035', '110036', '110057', '110062', '110063');
            }
            break;
          default:
            fieldsids = [];
        }
        if (!hasUncoveredPF) {
          fieldsids = fieldsids.filter(id => id !== '110062');
        }
        if (!hasUncoveredPT) {
          fieldsids = fieldsids.filter(id => id !== '110063');
        }
        return fieldsids;
      };

      const getSystemsTaskPerformance = (systemIds: string[]): Promise<LatestSystemsEnergyAnalysisTasksResponse> => {
        return new Promise<LatestSystemsEnergyAnalysisTasksResponse>((resolve, reject) => {
          energyAnalysesService.getLatestSystemsEnergyAnalysisTasks(systemIds).subscribe({
            next: response => {
              resolve(response);
              // tasks performance
              const energyAnalysesTasks = response.systemEnergyAnalysisTasks;
              // check if at least one system has boiler and efc
              let hasBoiler = false;
              let hasEFC = false;

              if (energyAnalysesTasks) {
                for (let i = 0; i < energyAnalysesTasks.length; i++) {
                  const item = energyAnalysesTasks[i];
                  const system = store.systems().elements.find(sys => sys.id === item.systemId);
                  if (system?.unitsFlags?.hasBoiler) {
                    hasBoiler = true;
                  }
                  if (system?.unitsFlags?.hasChillerEFC) {
                    hasEFC = true;
                  }
                }
              }

              if (energyAnalysesTasks && energyAnalysesTasks.length > 0) {
                // colDef
                const performanceColDef: any[] = [];
                const binFields = [110028];
                const heatingCapacityField = {
                  dataType: 1,
                  descriptionId: 451708,
                  elcaDescriptionId: 1,
                  id: 110006,
                  unitOfMeasurement: store.activeEnergyAnalysis()?.heatingLoadUomSymbol ?? '',
                  unitOfMeasurementGroupId: 1,
                };
                const coolingCapacityField = {
                  dataType: 1,
                  descriptionId: 451711,
                  elcaDescriptionId: 1,
                  id: 110005,
                  unitOfMeasurement: store.activeEnergyAnalysis()?.coolingLoadUomSymbol ?? '',
                  unitOfMeasurementGroupId: 1,
                };
                if (store.activeEnergyAnalysis()?.systemUsageType.id === '1') {
                  if (hasEFC) {
                    binFields.push(110029, 110030, 110005, 110032);
                  } else {
                    binFields.push(110030, 110005, 110032);
                  }
                  if (energyAnalysesTasks[0]?.systemSvcResult?.fields) {
                    energyAnalysesTasks[0].systemSvcResult.fields['110005'] = coolingCapacityField;
                  }
                } else if (store.activeEnergyAnalysis()?.systemUsageType.id === '2') {
                  binFields.push(110030, 110006, 110034);
                  if (energyAnalysesTasks[0]?.systemSvcResult?.fields) {
                    energyAnalysesTasks[0].systemSvcResult.fields['110006'] = heatingCapacityField;
                  }
                } else if (store.activeEnergyAnalysis()?.systemUsageType.id === '3') {
                  if (hasEFC) {
                    binFields.push(110029, 110030, 110005, 110006, 110032, 110034);
                  } else {
                    binFields.push(110030, 110005, 110006, 110032, 110034);
                  }

                  if (energyAnalysesTasks[0]?.systemSvcResult?.fields) {
                    energyAnalysesTasks[0].systemSvcResult.fields['110006'] = heatingCapacityField;
                    energyAnalysesTasks[0].systemSvcResult.fields['110005'] = coolingCapacityField;
                  }
                }

                if (energyAnalysesTasks[0]?.systemSvcResult?.fields) {
                  // binFieldsColDef
                  const binFieldsColDef: ColDef[] = [];
                  binFields.forEach(fieldId => {
                    if (energyAnalysesTasks[0].systemSvcResult?.fields?.[fieldId]) {
                      binFieldsColDef.push({
                        field: fieldId.toString(),
                        type: [ApmGridColumnType.WHITE_BACKGROUND],
                        headerClass: 'ag-header-label-centered text-center justify-content-center',
                        headerName: `${resourcesService.getTextResource(energyAnalysesTasks[0].systemSvcResult.fields[fieldId].descriptionId)} (${energyAnalysesTasks[0].systemSvcResult.fields[fieldId].unitOfMeasurement})`,
                        headerTooltip: resourcesService.getTextResource(
                          energyAnalysesTasks[0].systemSvcResult.fields[fieldId].descriptionId
                        ),
                        width: 150,
                        wrapHeaderText: true,
                        autoHeaderHeight: true,
                        valueFormatter: (params: ValueFormatterParams) =>
                          formatEaNumber(params.value, getDetailsDecimals(fieldId, params.value)),
                        pinned: 'left',
                      });
                    }
                  });
                  performanceColDef.push({
                    field: 'bin',
                    groupId: 'bin',
                    marryChildren: true,
                    type: [ApmGridColumnType.WHITE_BACKGROUND],
                    headerClass: 'ag-header-label-centered justify-content-center',
                    headerTooltip: resourcesService.getTextResource('448871'),
                    headerName: resourcesService.getTextResource('448871'),
                    maxWidth: 120,
                    children: binFieldsColDef,
                  });
                }
                const svcRequests: any[] = [];
                const systemSvcResults: any[] = [];

                energyAnalysesTasks.forEach((item, index) => {
                  const system = store.systems().elements.find(sys => sys.id === item.systemId);
                  const cols = getColumnsForSystemTaskPerformance(
                    system!,
                    hasBoiler,
                    item.systemSvcResult?.hasUncoveredPF ?? false,
                    item.systemSvcResult?.hasUncoveredPT ?? false
                  );
                  let systemColDefs: ColDef[] = [];

                  svcRequests.push({
                    svcRequest: item.svcRequest,
                    systemId: item.systemName,
                  });

                  systemSvcResults.push({
                    systemSvcResult: item.systemSvcResult,
                    systemId: item.systemName,
                  });

                  if (item.systemSvcResult?.fields) {
                    for (const key of cols) {
                      const value = item.systemSvcResult.fields[Number(key)];
                      if (!binFields.includes(Number(key)) && value) {
                        systemColDefs.push({
                          field: `${key}_${item.systemId}`,
                          type: [ApmGridColumnType.WHITE_BACKGROUND],
                          headerClass: 'ag-header-label-centered text-center justify-content-center',
                          headerName: `${resourcesService.getTextResource(value.descriptionId)} (${value.unitOfMeasurement})`,
                          headerTooltip: resourcesService.getTextResource(value.descriptionId),
                          width: 150,
                          wrapHeaderText: true,
                          autoHeaderHeight: true,
                          valueFormatter: (params: ValueFormatterParams) =>
                            formatEaNumber(params.value, getDetailsDecimals(key, params.value)),
                          cellStyle: param => {
                            let opacity = 0.3;
                            if ((key === '110062' || key === '110063') && param.value > 0) {
                              opacity = 0.7;
                            }
                            item.systemId;
                            const color = store.systemsColors()[item.systemId || '']?.value;
                            return color
                              ? {
                                  'background-color': getColorWithOpacity(color, opacity),
                                  'text-align': 'center',
                                }
                              : null;
                          },
                        });
                      }
                    }
                  }
                  performanceColDef.push({
                    field: item.systemName,
                    // APPLYME-6304: the grid state names the group by system id; its columns stay inside it, and
                    // every system follows the column layout the user sets on one of them
                    groupId: item.systemId,
                    marryChildren: true,
                    type: [ApmGridColumnType.WHITE_BACKGROUND],
                    headerClass: 'ag-header-label-centered justify-content-center',
                    headerTooltip: item.systemName,
                    headerName: item.systemName,
                    children: systemColDefs,
                  });
                });

                const dataMap = new Map<number, PerformanceData>();

                // totals
                let totalsFields: {
                  field: number;
                  idResource: number;
                  unit: string;
                }[] = [];
                const systemsTotals: any[] = [];
                // unit of measure comes from the backend quantity (kWh in SI, converted in IP);
                // it is the same across systems, so pick the first task that provides it.
                const findTotalsUnit = (fieldId: number, isAverage = false): string => {
                  for (const task of energyAnalysesTasks) {
                    const quantityField = isAverage
                      ? task.systemSvcResult?.fieldsAverages?.[fieldId]
                      : task.systemSvcResult?.fieldsTotals?.[fieldId];
                    if (quantityField?.quantity?.unit) {
                      return quantityField.quantity.unit;
                    }
                  }
                  return '';
                };
                totalsFields = [
                  {
                    field: 1000000000,
                    idResource: 448879,
                    unit: findTotalsUnit(1000000000),
                  },
                  {
                    field: 1000000001,
                    idResource: 448880,
                    unit: findTotalsUnit(1000000001, true),
                  },
                ];
                if (hasBoiler) {
                  totalsFields.push({
                    field: 1000000002,
                    idResource: 451703,
                    unit: findTotalsUnit(1000000002),
                  });
                }
                patchState(store, {
                  systemsTotalsFields: totalsFields,
                });

                const systemsColors = store.systemsColors();
                Object.entries(systemsColors).forEach(([key, value]) => {
                  if (!energyAnalysesTasks.find(task => task.systemId === key)) {
                    systemsColors[key].active = false;
                  } else {
                    systemsColors[key].active = true;
                  }
                });

                patchState(store, {
                  systemsColors,
                });

                energyAnalysesTasks.forEach((systemItem, index) => {
                  let systemBins = systemItem.svcRequest?.analysisInput?.groups?.find(
                    system => system.groupUID === systemItem.systemId
                  ).bin;
                  const systemColor = store.systemsColors()[systemItem.systemId || '']?.value;
                  systemItem.systemSvcResult?.bins?.forEach(binItem => {
                    const requestBin = systemBins?.find((b: any) => b.binuid == binItem.id);

                    const heatingCapacity = requestBin.workingConditions.find((wc: any) => wc.id === 110006);
                    const coolingCapacity = requestBin.workingConditions.find((wc: any) => wc.id === 110005);

                    const binKey = binItem.values[110028]?.value;
                    if (binKey === undefined) return;

                    // Ottieni o crea la riga per questo bin
                    let rowData = dataMap.get(binKey);

                    if (!rowData) {
                      rowData = {};
                      dataMap.set(binKey, rowData);
                    }

                    // add heating capacity value if exists
                    rowData[110006] = heatingCapacity ? heatingCapacity.value : 0;
                    // add cooling capacity value if exists
                    rowData[110005] = coolingCapacity ? coolingCapacity.value : 0;

                    // Aggiungi tutti i valori
                    Object.entries(binItem.values).forEach(([key, val]) => {
                      if (!val) return;

                      const numKey = Number(key);
                      if (binFields.includes(numKey)) {
                        // Campo condiviso (solo se non esiste già)
                        if (!(key in rowData!)) {
                          rowData![key] = val.value;
                        }
                      } else {
                        // Campo specifico del sistema
                        rowData![`${key}_${systemItem.systemId}`] = val.value;
                      }
                    });
                  });
                  const totalData = systemItem.systemSvcResult?.fieldsTotals?.[totalsFields[0].field];
                  const averageData = systemItem.systemSvcResult?.fieldsAverages?.[totalsFields[1].field];
                  if (hasBoiler) {
                    const annualPEATotal = systemItem.systemSvcResult?.fieldsTotals?.[1000000002]?.quantity.value;
                    systemsTotals.push({
                      title: systemItem?.systemName || '',
                      [totalsFields[0].field]: totalData ? totalData.quantity.value : '-',
                      [totalsFields[1].field]: averageData ? averageData.quantity.value : '-',
                      [1000000002]: annualPEATotal,
                      color: systemColor || undefined,
                    });
                  } else {
                    systemsTotals.push({
                      title: systemItem?.systemName || '',
                      [totalsFields[0].field]: totalData ? totalData.quantity.value : '-',
                      [totalsFields[1].field]: averageData ? averageData.quantity.value : '-',
                      color: systemColor || undefined,
                    });
                  }
                });

                // Converti la Map in array e ordina
                const data: PerformanceData[] = Array.from(dataMap.values()).sort(
                  (a, b) => Number(a[110028]) - Number(b[110028])
                );
                const taskStatusId = energyAnalysesTasks[0].statusId;

                patchState(store, {
                  taskPerformanceColDef: performanceColDef,
                  tasksPerformanceData: data,
                  taskStatusId,
                  systemsTotals,
                  svcRequests,
                  systemSvcResults,
                });

                // for every curves
                const curves: {
                  systemName: string;
                  curves: any[];
                  systemId: string;
                }[] = [];

                energyAnalysesTasks.forEach(item => {
                  if (item.systemSvcResult?.curves) {
                    const activeAnalysis = store.activeEnergyAnalysis();
                    const maxCoolingCapacity = activeAnalysis?.maxCoolingLoadDemand;
                    const maxHeatingCapacity = activeAnalysis?.maxHeatingLoadDemand;
                    const systemCurves = item.systemSvcResult.curves;
                    // Temperature curve (110028): the entry key is the chart X-axis label while the
                    // entry value is the bin temperature, which is the key used by dataMap. The
                    // cooling/heating load lines must follow the per-bin load profile (ids 110005/110006,
                    // i.e. loadRatio * maxLoadDemand) already collected in dataMap, not the flat peak load.
                    const temperatureData: { [key: string]: number } = systemCurves['110028']?.data || {};

                    systemCurves['110005'] = {
                      id: '110005',
                      keyLabel: '454789',
                      keyUom: activeAnalysis?.airTemperatureUomSymbol ?? '°C',
                      name: '451711',
                      valueLabel: '451711',
                      valueUom: activeAnalysis?.coolingLoadUomSymbol ?? '',
                      data: Object.fromEntries(
                        Object.entries(temperatureData).map(([key, temperature]) => [
                          key,
                          dataMap.get(temperature)?.[110005] ?? maxCoolingCapacity,
                        ])
                      ),
                    };
                    systemCurves['110006'] = {
                      id: '110006',
                      keyLabel: '454789',
                      keyUom: activeAnalysis?.airTemperatureUomSymbol ?? '°C',
                      name: '451708',
                      valueLabel: '451708',
                      valueUom: activeAnalysis?.heatingLoadUomSymbol ?? '',
                      data: Object.fromEntries(
                        Object.entries(temperatureData).map(([key, temperature]) => [
                          key,
                          dataMap.get(temperature)?.[110006] ?? maxHeatingCapacity,
                        ])
                      ),
                    };

                    curves.push({
                      systemName: item.systemName || '',
                      systemId: item.systemId || '',
                      curves: item.systemSvcResult.curves,
                    });
                  }
                });
                patchState(store, {
                  systemSvcResultCurves: curves,
                });
                // use only the first for chart fields
                if (energyAnalysesTasks[0]) {
                  if (energyAnalysesTasks[0].systemSvcResult?.fields) {
                    patchState(store, {
                      taskPerformanceFields: energyAnalysesTasks[0].systemSvcResult.fields,
                    });
                  }
                }
              } else {
                patchState(store, {
                  taskPerformanceColDef: [],
                  tasksPerformanceData: [],
                  taskStatusId: 0,
                  systemsTotals: [],
                  svcRequests: [],
                  systemSvcResults: [],
                  systemSvcResultCurves: [],
                  taskPerformanceFields: {},
                });
              }

              // economic analyses
              const economicAnalyses = response.economicAnalysis;
              if (economicAnalyses) {
                const fields: {
                  valueYField: string;
                  valueXField: string;
                  label?: string;
                  color?: string;
                  fills?: boolean;
                  hideLegend?: boolean;
                  bulletType?: string;
                  bulletRadius?: number;
                }[] = [];
                const data: { [key: string]: number | string | null }[] = [];
                let info: PaybackInfo | null = {};
                const paybackDataset = economicAnalyses.result?.datasetsGroups?.find(
                  group => group.id === 'payback-summary'
                );
                info.tabLabel = paybackDataset?.name || '';

                paybackDataset?.datasets?.forEach((dataset, index) => {
                  if (dataset.type === 'NUMERIC_CURVE') {
                    fields.push({
                      valueYField: dataset.id,
                      valueXField: 'categoryX',
                      label: dataset.name,
                      color: store.systemsColors()[dataset.id]?.value || '#000000',
                      hideLegend: false,
                    });

                    info.keyLabel = resourcesService.getTextResource(dataset.keyLabel);
                    info.keyUnit = dataset.keyUom;
                    info.valueLabel = resourcesService.getTextResource(dataset.valueLabel);
                    info.valueUnit = dataset.valueUom;
                  } else if (dataset.type === 'POINT_2D') {
                    fields.push({
                      valueYField: dataset.name,
                      valueXField: 'categoryX',
                      color: '#ff0000',
                      bulletType: 'diamond',
                      bulletRadius: 4,
                      hideLegend: true,
                    });
                  }
                  if (dataset.data) {
                    Object.entries(dataset?.data).forEach(([key, val]) => {
                      if (data.find(item => item['categoryX'] == parseFloat(key))) {
                        const existingData = data.find(item => item['categoryX'] === parseFloat(key));
                        if (existingData) {
                          existingData[dataset.id] = val as number;
                        }
                      } else {
                        data.push({
                          categoryX: parseFloat(key),
                          [dataset.id]: val as number,
                        });
                      }
                    });
                  } else if (dataset.x && dataset.y) {
                    data.push({
                      categoryX: dataset.x,
                      [dataset.name]: dataset.y,
                      xLabel: dataset.xlabel || '',
                      yLabel: dataset.ylabel || '',
                    });
                  }
                  patchState(store, {
                    energyAnalysisPaybackFields: fields,
                    energyAnalysisPaybackData: data,
                    energyAnalysisPaybackInfo: info,
                  });
                });

                const gropComparisonFields = economicAnalyses.result?.datasetsGroups?.find(
                  group => group.id === 'systems-comparison-section'
                );
                const comparisonInfo: GroupComparisonInfo = {
                  title: gropComparisonFields?.name || '',
                };

                const chartData: { [key: string]: number | string | null }[] = [];

                const columnDesc: {
                  valueYField: string;
                  categoryXField: string;
                  label?: string;
                  color?: string;
                }[] = [];
                gropComparisonFields?.datasets.forEach((dataset, index) => {
                  if (dataset.type === 'TABLE') {
                    comparisonInfo.columnsDef = dataset.columnsDefinitions;
                    comparisonInfo.columnsRows = dataset.rows?.map(row => {
                      return {
                        ...row,
                      };
                    });
                  }
                  if (dataset.type === 'HISTOGRAM') {
                    columnDesc.push({
                      valueYField: dataset.id,
                      categoryXField: 'categoryX',
                      label: dataset.name,
                      color: store.systemsColors()[dataset.id]?.value,
                    });
                    comparisonInfo.chartLabels = {
                      keyLabel: resourcesService.getTextResource(dataset.keyLabel),
                      keyUnit: dataset.keyUom,
                      valueLabel: resourcesService.getTextResource(dataset.valueLabel),
                      valueUnit: dataset.valueUom,
                    };
                  }
                  dataset.bins?.forEach(bin => {
                    let valueKey = 'discounted_cost_per_year';
                    Object.keys(bin.value).forEach(key => {
                      valueKey = key; // to avoid ts error
                    });
                    if (chartData.find(item => item['categoryX'] == bin.key)) {
                      const existedBin = chartData.find(item => item['categoryX'] == bin.key);
                      if (existedBin) {
                        existedBin[dataset.id] = bin.value[valueKey];
                      }
                    } else {
                      chartData.push({
                        categoryX: bin.key,
                        // inserire dinamicamente il campo. è sempre solo uno?
                        [dataset.id]: bin.value[valueKey],
                      });
                    }
                  });
                });
                // cumulative cost (same curves as payback) and cash flow vs the reference (first) system
                const translateChartLabels = (chart: EconomicCurvesChart): EconomicCurvesChart => ({
                  ...chart,
                  keyLabel: resourcesService.getTextResource(chart.keyLabel),
                  valueLabel: resourcesService.getTextResource(chart.valueLabel),
                });
                const cumulativeChart = buildEconomicCurvesChart(paybackDataset, store.systemsColors());
                const cashFlowChart = buildEconomicCurvesChart(gropComparisonFields, store.systemsColors());
                cashFlowChart.series = cashFlowChart.series.map((serie, index) =>
                  index === 0
                    ? { ...serie, label: `${serie.label} (${resourcesService.getTextResource('520941')})` }
                    : serie
                );
                patchState(store, {
                  energyAnalysisGroupComparisonInfo: comparisonInfo,
                  energyAnalysisGroupComparisonData: chartData,
                  energyAnalysisGroupComparisonColumns: columnDesc,
                  energyAnalysisGroupComparisonCumulativeChart: translateChartLabels(cumulativeChart),
                  energyAnalysisGroupComparisonCashFlowChart: translateChartLabels(cashFlowChart),
                });
              } else {
                patchState(store, {
                  energyAnalysisPaybackData: [],
                  energyAnalysisPaybackFields: [],
                  energyAnalysisPaybackInfo: null,
                  energyAnalysisGroupComparisonData: [],
                  energyAnalysisGroupComparisonColumns: [],
                  energyAnalysisGroupComparisonInfo: null,
                  energyAnalysisGroupComparisonCumulativeChart: null,
                  energyAnalysisGroupComparisonCashFlowChart: null,
                });
              }
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };
      const getSystemWorkingModes = (): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.getSystemWorkingMode().subscribe({
            next: response => {
              resolve();
              patchState(store, {
                systemWorkingModes: response,
              });
            },
            error: error => {
              console.error('Error deleting system unit', error);
              reject(error);
            },
          });
        });
      };

      const loadSystemsComparisonSettings = (energyAnalysisId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.getSystemComparisonSettings(energyAnalysisId).subscribe({
            next: response => {
              resolve();
              patchState(store, {
                systemsComparisonSettings: response.systemsComparisonSectionSettings,
              });
            },
            error: error => {
              console.error('Error loading systems comparison settings', error);
              reject(error);
            },
          });
        });
      };

      const updateSystemsComparisonSettings = (
        energyAnalysisId: string,
        updateSettings: UpdateSystemsComparisonSettingsRequest
      ): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          energyAnalysesService.updateSystemComparisonSettings(energyAnalysisId, updateSettings).subscribe({
            next: response => {
              resolve();
              patchState(store, {
                systemsComparisonSettings: response.systemsComparisonSectionSettings,
              });
            },
            error: error => {
              console.error('Error updating systems comparison settings', error);
              reject(error);
            },
          });
        });
      };

      const cleanActiveEnergyAnalysis = () => {
        patchState(store, {
          activeEnergyAnalysis: null,
          activeSystem: null,
          activeSystemUnit: null,
          systemUnits: [],
          totalSystemUnits: 0,
          systemUsagePlanGrid: [],
          systemUsagePlanHistogram: [],
          systemUsagePlanBinsData: [],
          systemUsagePlanBinsTotalOperatingHours: 0,
          capacityCurveData: [],
          cityClimateData: [],
          activeTaskPerformanceCol: [],
          tasksPerformanceData: [],
          taskStatusId: 0,
          energyAnalysisPaybackData: [],
          energyAnalysisPaybackFields: [],
          energyAnalysisPaybackInfo: null,
          systemsTotals: [],
          systemsTotalsFields: [],
          systemSvcResultCurves: [],
          taskPerformanceFields: null,
          activeTaskStatus: null,
        });
      };
      const updateactiveTaskStatus = (task: Task) => {
        patchState(store, {
          activeTaskStatus: task,
        });
      };

      const cleanActiveSystemUnit = () => {
        patchState(store, {
          activeSystemUnit: null,
        });
      };

      const cleanTasksPerformanceData = () => {
        patchState(store, {
          tasksPerformanceData: [],
          taskStatusId: 0,
          energyAnalysisPaybackData: [],
          energyAnalysisPaybackFields: [],
          energyAnalysisPaybackInfo: null,
          systemsTotals: [],
          systemSvcResultCurves: [],
          taskPerformanceFields: null,
          activeTaskStatus: null,
        });
      };

      const updateTaskStatus = (taskStatusId: 0 | 1 | 2 | 3 | 4 | 5 | 6) => {
        patchState(store, {
          taskStatusId: taskStatusId,
        });
      };

      return {
        loadEnergyAnalyses,
        loadNextPage: () => {
          if (!store.isFetching() && store.paginationHasNextPage()) {
            patchState(store, { currentPage: store.currentPage() + 1 });
            loadEnergyAnalyses();
          }
        },
        updateEnergyAnalysisFilters,
        getEnergyAnalysis: (energyAnalysisId: string) => {
          getEnergyAnalysis(energyAnalysisId).then(() => {
            getEnergyAnalysisConfig(energyAnalysisId);
          });
        },
        getEnergyAnalysisConfig,
        updateEnergyAnalysis,
        setEconomicParameters,
        setAllowSaveEconomicParameters,
        saveEconomicDefaults,
        getSystemEconomicDefaults,
        setRowsManualBinsTable,
        setAllowSaveManualBinsTable,
        updateManualBinsTableAndRefresh: (updateManualBinsTableData: UpdateManualBinsTable[]) => {
          updateManualBinsTable(updateManualBinsTableData).then(response => {
            setRowsManualBinsTable(response.systemUsagePlanBinsData);
            setAllowSaveManualBinsTable(false);
            loadSystemUsagePlanHistogram();
          });
        },

        loadEnergyAnalysisAndSystems: async (energyAnalysisId: string): Promise<void> => {
          await getEnergyAnalysis(energyAnalysisId);
          await Promise.all([loadSystems(), getEnergyAnalysisConfig(energyAnalysisId)]);
        },
        // Currency change: the symbol on every price is derived from activeEnergyAnalysis().currencyCode, which
        // the PATCH flips immediately, while the converted values only arrive after the systems reload. Pin the
        // displayed symbol to the PREVIOUS currency for the whole flow and release it only once the reload has
        // landed, so symbol and values flip together (no "new symbol / old values" window). Mirrors Study.
        changeCurrencyCodeAndRefresh: async (energyAnalysisId: string, currencyCode: string): Promise<void> => {
          const previous = store.activeEnergyAnalysis()?.currencyCode ?? null;
          patchState(store, { displayCurrencyCodeOverride: previous });
          spinnerService.showMainSpinner();
          try {
            await updateEnergyAnalysis(energyAnalysisId, { currencyCode });
            await getEnergyAnalysis(energyAnalysisId);
            await Promise.all([loadSystems(), getEnergyAnalysisConfig(energyAnalysisId)]);
          } finally {
            patchState(store, { displayCurrencyCodeOverride: null });
            spinnerService.hideMainSpinner();
          }
        },
        getEnergyAnalysisSystem,
        loadEnergyAnalysisSystemAndUnits: (systemId: string) => {
          getEnergyAnalysisSystem(systemId).then(() => {
            searchSystemUnits();
          });
        },
        getEnergyAnalysisSystemUnit,
        loadPresets,
        createPresetAndRefresh: (presetName: string) => {
          createPreset(presetName).then(() => {
            loadPresets();
          });
        },
        deletePresetAndRefresh: (presetId: string) => {
          onlyDeletePreset(presetId).then(() => {
            loadPresets();
          });
        },
        loadPresetAndRefreshRulesAndDataGrid: (presetId: string) => {
          loadPreset(presetId).then(async () => {
            loadSysthemUsagePlanGrid();
            loadEnergyAnalysisSystemUsagePlanBinsTable();
            loadEnergyAnalysisRules();
            loadSystemUsagePlanHistogram();
            // reload capacity curve
            const { coolingCurve, labels: coolingLabels } = await loadCoolingCapacityCurve();
            const { heatingCurve, labels: heatingLabels } = await loadHeatingCapacityCurve();
            let dataChart: CapacityCurveData[] = [];
            for (const [key, value] of Object.entries(coolingCurve)) {
              dataChart.push({
                temperature: Number(key),
                coolingPower: value * 100,
                thermalPower: heatingCurve[key] * 100,
              });
            }
            const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);

            patchState(store, {
              capacityCurveData: sortedDataChart,
              capacityCurveDataLabels: {
                keyLabel: coolingLabels.keyLabel || heatingLabels.keyLabel || '',
                keyUom: coolingLabels.keyUom || heatingLabels.keyUom || '',
                valueLabel: coolingLabels.valueLabel || heatingLabels.valueLabel || '',
                valueUom: coolingLabels.valueUom || heatingLabels.valueUom || '',
              },
            });
          });
        },
        loadDataRulesAndRefreshRulesAndCharts: () => {
          loadSysthemUsagePlanGrid();
          loadEnergyAnalysisSystemUsagePlanBinsTable();
          loadEnergyAnalysisRules();
          loadSystemUsagePlanHistogram();
        },
        loadCityClimateDataAndRefreshMainChartData: async (cityId: string | null | undefined) => {
          if (!cityId) {
            patchState(store, {
              cityClimateData: [],
              capacityCurveData: [],
              capacityCurveDataLabels: null,
            });
            // In manual mode the histogram and bins come from the manual table, not the city
            if (store.activeEnergyAnalysis()?.manualClimateDataBinsTable) {
              loadSystemUsagePlanHistogram();
              loadEnergyAnalysisSystemUsagePlanBinsTable();
            } else {
              patchState(store, {
                systemUsagePlanHistogram: [],
                systemUsagePlanBinsData: [],
                systemUsagePlanBinsTotalOperatingHours: 0,
              });
            }
            return;
          }
          const params: ClimateDataRequest = {
            ...store.cityClimateDataParams(),
            projectId: store.activeEnergyAnalysis()?.projectId,
          };
          energyAnalysesService.getClimateData(cityId, params).subscribe({
            next: async response => {
              if (!response) {
                return;
              }
              const storeCityClimateData: cityChartData[] = response.content.bins.map(item => ({
                temperature: item.key,
                hours: item.value.measurement_hours,
              }));
              const sortedCityClimateData = [...storeCityClimateData].sort((a, b) => a.temperature - b.temperature);

              patchState(store, {
                cityClimateData: sortedCityClimateData,
              });

              // Load capacity curve data and histogram
              if (store.activeEnergyAnalysis()) {
                loadSystemUsagePlanHistogram();
                loadEnergyAnalysisSystemUsagePlanBinsTable();
                const { coolingCurve, labels: coolingLabels } = await loadCoolingCapacityCurve();
                const { heatingCurve, labels: heatingLabels } = await loadHeatingCapacityCurve();
                let dataChart: CapacityCurveData[] = [];
                for (const [key, value] of Object.entries(coolingCurve)) {
                  dataChart.push({
                    temperature: Number(key),
                    coolingPower: value * 100,
                    thermalPower: heatingCurve[key] * 100,
                  });
                }
                const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);
                let valueLabel = '';
                if (store.activeEnergyAnalysis()?.systemType.id !== '2') {
                  // add cooling
                  valueLabel = resourcesService.getTextResource(coolingLabels.valueLabel);
                }
                if (store.activeEnergyAnalysis()?.systemType.id !== '1') {
                  // add heating
                  if (valueLabel !== '') {
                    valueLabel += ' - ';
                  }
                  valueLabel += resourcesService.getTextResource(heatingLabels.valueLabel);
                }

                const valueUom = coolingLabels.valueUom || heatingLabels.valueUom || '';
                const keyLabel = coolingLabels.keyLabel || heatingLabels.keyLabel || '';
                const keyUom = coolingLabels.keyUom || heatingLabels.keyUom || '';

                patchState(store, {
                  capacityCurveData: sortedDataChart,
                  capacityCurveDataLabels: {
                    keyLabel,
                    keyUom,
                    valueLabel,
                    valueUom,
                  },
                });
              }
            },
            error: error => {
              console.error('Error loading city climate data', error);
            },
          });
        },
        loadCapacityCurve: async () => {
          const { coolingCurve, labels: coolingLabels } = await loadCoolingCapacityCurve();
          const { heatingCurve, labels: heatingLabels } = await loadHeatingCapacityCurve();
          let dataChart: CapacityCurveData[] = [];
          for (const [key, value] of Object.entries(coolingCurve)) {
            dataChart.push({
              temperature: Number(key),
              coolingPower: value * 100,
              thermalPower: heatingCurve[key] * 100,
            });
          }
          const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);

          patchState(store, {
            capacityCurveData: sortedDataChart,
            capacityCurveDataLabels: {
              keyLabel: coolingLabels.keyLabel || heatingLabels.keyLabel || '',
              keyUom: coolingLabels.keyUom || heatingLabels.keyUom || '',
              valueLabel: coolingLabels.valueLabel || heatingLabels.valueLabel || '',
              valueUom: coolingLabels.valueUom || heatingLabels.valueUom || '',
            },
          });
        },
        updateCapacityCurveFromCooling: async (updateCapacityCurve: { key: number; value: number }) => {
          const { coolingCurve } = await updateCoolingCapacityCurve(updateCapacityCurve);
          const dataChart: CapacityCurveData[] = [];
          const existingData = store.capacityCurveData();

          for (const [key, value] of Object.entries(coolingCurve)) {
            const existingRow = existingData.find(item => item.temperature === Number(key));
            dataChart.push({
              temperature: Number(key),
              coolingPower: value * 100,
              thermalPower: existingRow?.thermalPower || 0,
            });
          }

          const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);
          // TODO: optimize with only update the changed point instead of reload all the curve and all the charts that depend on it
          loadEnergyAnalysisSystemUsagePlanBinsTable();

          patchState(store, {
            capacityCurveData: [...sortedDataChart],
          });
        },
        updateCapacityCurveFromHeating: async (updateCapacityCurve: { key: number; value: number }) => {
          const { heatingCurve } = await updateHeatingCapacityCurve(updateCapacityCurve);
          const dataChart: CapacityCurveData[] = [];
          const existingData = store.capacityCurveData();

          for (const [key, value] of Object.entries(heatingCurve)) {
            const existingRow = existingData.find(item => item.temperature === Number(key));
            dataChart.push({
              temperature: Number(key),
              coolingPower: existingRow?.coolingPower || 0,
              thermalPower: value * 100,
            });
          }
          const sortedDataChart = [...dataChart].sort((a, b) => a.temperature - b.temperature);
          // TODO: optimize with only update the changed point instead of reload all the curve and all the charts that depend on it
          loadEnergyAnalysisSystemUsagePlanBinsTable();

          patchState(store, {
            capacityCurveData: [...sortedDataChart],
          });
        },
        loadSystemUsagePlanHistogram,
        loadEnergyAnalysisSystemUsagePlanBinsTable,
        deleteAndRefreshSystemUnit: (unitId: string) => {
          deleteSystemUnit(unitId).then(() => {
            getEnergyAnalysisSystem(store.activeSystem()!.id).then(() => {
              searchSystemUnits();
            });
          });
        },
        updateSystemUnitandRefresh: (unitId: string, updateSystemUnitRequest: UpdateSystemUnitRequest) => {
          updateSystemUnit(unitId, updateSystemUnitRequest).then(() => {
            getEnergyAnalysisSystem(store.activeSystem()!.id);
            searchSystemUnits();
          });
        },
        updateSystem,
        deleteAndRefreshSystem: (systemId: string) => {
          return new Promise<void>((resolve, reject) => {
            deleteSystem(systemId)
              .then(() => {
                loadSystems()
                  .then(() => {
                    resolve();
                  })
                  .catch(reject);
              })
              .catch(reject);
          });
        },
        copyAndRefreshSystem: (systemId: string) => {
          // the analysis too: its systemsCount hides the copy command at the limit of 5
          return copySystem(systemId).then(copy =>
            Promise.all([
              loadSystems(),
              store.activeEnergyAnalysis() ? getEnergyAnalysis(store.activeEnergyAnalysis()!.id) : Promise.resolve(),
            ]).then(() => copy)
          );
        },
        getSystemTaskPerformance: (energyAnalysesSystemId: string) => {
          return getSystemsTaskPerformance([energyAnalysesSystemId]).then(() => {
            if (store.activeEnergyAnalysis()) {
              getEnergyAnalysis(store.activeEnergyAnalysis()!.id);
            }
          });
        },
        getSystemsTaskPerformance,
        updateactiveTaskStatus,
        cleanActiveEnergyAnalysis,
        cleanActiveSystemUnit,
        cleanTasksPerformanceData,
        getSystemWorkingModes,
        loadSystemsSettingsAndPerformances: (energyAnalisisId: string) => {
          loadSystemsComparisonSettings(energyAnalisisId).then(() => {
            getSystemsTaskPerformance(store.selectedSystemsIds());
          });
        },
        updateSystemsSettingsAndPerformances: (
          energyAnalisisId: string,
          updateSettings: UpdateSystemsComparisonSettingsRequest
        ) => {
          updateSystemsComparisonSettings(energyAnalisisId, updateSettings).then(() => {
            getSystemsTaskPerformance(store.selectedSystemsIds());
          });
        },
        updateTaskStatus,
      };
    }
  )
);
